// league-authority.js — the ONE league authority for grading.
//
// Single job: given every league signal a bet carries (bets.league root, each
// bet_legs.league, each resolved game_cache.league), decide the single league that
// grading may use, or refuse. Imported by BOTH api/lib/settlement-processor.js and
// the client grading consumers (betting.js, SlipTicket, BookiesPage, Dashboard,
// Results) so the two authorities cannot drift (#1037 round 3 P1).
//
// Never-reject r4 R2: the settlement processor passes TRACKED legs only -- an untracked
// leg keeps its real league and is never a mismatch signal.
//
// It reads nothing and writes nothing. The caller supplies `known` (server:
// KNOWN_LEAGUES; client: SUPPORTED_LEAGUES) and applies its own staged-league
// policy to the single league this returns.
//
// Precedence (Ultron amendment 1, ratified 2026-09-11; inverts GPT's rule 1 which
// would have classified a corrupt NHL bet on an NFL game as a healthy NFL skip):
//   1. normalize every signal; null/undefined/non-string/not-known -> UNKNOWN, dropped
//   2. more than one distinct known league     -> { status: 'mismatch', leagues, signals }
//   3. exactly one known league                 -> { status: 'ok', league }
//   4. no known league                          -> { status: 'unknown' }
//
// Normalization lives HERE ONLY. Callers must not normalize their own raw fields
// (settlement-processor's distinctLeagues / effectiveLeague stay raw -- normalizing
// them silently reverses the pinned mixed_leg_leagues behaviour and changes prop
// hydration, CC-DENY-012).

export function normalizeLeagueSignal(value) {
  if (typeof value !== 'string') return null;
  const v = value.trim().toLowerCase();
  return v === '' ? null : v;
}

/**
 * @param {object} p
 * @param {*} p.root           bets.league
 * @param {Array<*>} p.legLeagues   every bet_legs.league (or leg.league) for the bet
 * @param {Array<*>} p.gameLeagues  every resolved game's league (undefined/null = unknown)
 * @param {string[]} p.known   the league allow-list the caller recognises
 */
export function resolveLeagueAuthority({ root, legLeagues = [], gameLeagues = [], known = [] }) {
  const knownSet = new Set(known.map(normalizeLeagueSignal).filter(Boolean));
  const keep = (v) => {
    const n = normalizeLeagueSignal(v);
    return n !== null && knownSet.has(n) ? n : null;
  };

  const rootN = keep(root);
  const legsN = legLeagues.map(keep).filter(Boolean);
  const gamesN = gameLeagues.map(keep).filter(Boolean);

  const distinct = [...new Set([rootN, ...legsN, ...gamesN].filter(Boolean))].sort();

  if (distinct.length > 1) {
    return {
      status: 'mismatch',
      leagues: distinct,
      signals: { root: rootN, legs: legsN, games: gamesN },
    };
  }
  if (distinct.length === 1) return { status: 'ok', league: distinct[0] };
  return { status: 'unknown' };
}
